import { useMemo, useState } from "react";
import QuestionNode from "./QuestionNode";
import EdgeLayer from "./EdgeLayer";
import useCanvasViewport from "./useCanvasViewport";
import useNodeDrag from "./useNodeDrag";

export default function CanvasPlate({
    initialNodes = [],
    edges = [],
    minZoom = 0.1,
    maxZoom = 5,
    gridSize = 20,
    callbackSave,
    estimationGroups,
    stages,
    botErrors,
    gotUpdatedQuestion,
}) {
    const [choseNextQuestionMode, setChoseNextQuestionMode] = useState();

    const errorsByAnswerId = useMemo(() => {
        const map = {};
        for (const entry of botErrors || []) {
            if (entry.answerId == null) continue;
            if (!map[entry.answerId]) map[entry.answerId] = [];
            map[entry.answerId].push(entry);
        }
        return map;
    }, [botErrors]);

    const viewport = useCanvasViewport({ minZoom, maxZoom });
    const { nodes, startNodeDrag, handleDragMove, handleDragEnd } = useNodeDrag({
        initialNodes,
        screenToWorld: viewport.screenToWorld,
        onDragEnd: callbackSave,
    });

    const nodeMap = useMemo(() => {
        const map = {};
        for (const node of nodes) map[node.id] = node;
        return map;
    }, [nodes]);

    const handleMouseMove = (e) => {
        if (viewport.isPanning) {
            viewport.handlePanMove(e);
            return;
        }
        handleDragMove(e);
    };

    const handleMouseUp = () => {
        handleDragEnd();
        viewport.endPan();
    };

    return (
        <div
            ref={viewport.viewportRef}
            onMouseDown={viewport.startPan}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
                width: "100%",
                height: "100vh",
                overflow: "hidden",
                position: "relative",
                backgroundColor: "var(--bg-color)",
                backgroundImage:
                    "radial-gradient(var(--lines-color) 1px, transparent 1px)",
                backgroundSize: `${gridSize}px ${gridSize}px`,
                userSelect: "none",
                cursor: viewport.isPanning ? "grabbing" : "default",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    transform: `translate(${viewport.pan.x}px, ${viewport.pan.y}px) scale(${viewport.zoom})`,
                    transformOrigin: "0 0",
                    willChange: "transform",
                }}
            >
                <EdgeLayer edges={edges} nodeMap={nodeMap} />

                {nodes.map((node) => (
                    <div
                        key={node.id}
                        onMouseDown={(e) => startNodeDrag(e, node)}
                        style={{
                            position: "absolute",
                            left: node.x,
                            top: node.y,
                            width: node.width || "fit-content",
                            height: node.height || "fit-content",
                            borderRadius: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 500,
                            cursor: "grab",
                        }}
                    >
                        <QuestionNode
                            item={node.item}
                            estimationGroups={estimationGroups}
                            stages={stages}
                            errors={errorsByAnswerId[node.item.id]}
                            choseNextQuestionMode={choseNextQuestionMode}
                            setChoseNextQuestionMode={setChoseNextQuestionMode}
                            gotUpdatedQuestion={gotUpdatedQuestion}
                        />
                    </div>
                ))}
            </div>

            <div className="canvas-zoom-controls" onMouseDown={(e) => e.stopPropagation()}>
                <button type="button" className="canvas-zoom-btn" onClick={() => viewport.zoomBy(1.2)}>+</button>
                <span className="canvas-zoom-level">{Math.round(viewport.zoom * 100)}%</span>
                <button type="button" className="canvas-zoom-btn" onClick={() => viewport.zoomBy(1 / 1.2)}>−</button>
                <button type="button" className="canvas-zoom-btn canvas-zoom-reset" onClick={viewport.resetView}>reset</button>
            </div>
        </div>
    );
}
